import { computed } from '@angular/core';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, tap } from 'rxjs';
import { withReplicationPool } from './replication-pool.feature';

/**
 * Root SignalStore owning the single active WebRTC replication pool for custom spawn brackets. All pool
 * lifecycle, retry, and status logic comes from the shared {@link withReplicationPool} feature; this store only
 * pins the entity-specific `start` (topic + lazy import of the bracket replication factory) and the strict
 * host/joiner interpretation.
 *
 * `host`/`join` are `rxMethod`s: feed them an id signal/observable and they (re)start replication whenever it
 * changes, ignoring `null`/`undefined` (e.g. an editor before its document has loaded).
 */
export const BracketReplicationStore = signalStore(
    { providedIn: 'root' },
    withReplicationPool<string>({
        label: 'bracket',
        start: async (bracketId, { getDatabase, signalingUrl }) => {
            const { startBracketReplication } = await import('./bracket-webrtc-replication');

            return startBracketReplication(
                {
                    bracketId,
                    signalingServerUrl: signalingUrl,
                    topic: `bracket-${bracketId}`,
                },
                getDatabase()
            );
        },
    }),
    withComputed((store) => ({
        isHost: computed(() => store.role() === 'host'),
    })),
    withMethods((store) => ({
        host: rxMethod<string | null | undefined>(
            pipe(
                filter((id): id is string => !!id),
                tap((id) => store._connect(id, 'host'))
            )
        ),
        join: rxMethod<string | null | undefined>(
            pipe(
                filter((id): id is string => !!id),
                tap((id) => store._connect(id, 'joiner'))
            )
        ),
    }))
);
